/**
 * search-fallback.js — Custom Piero
 *
 * Devuelve el buscador nativo de Shopify cuando APISearch no responde.
 *
 * Mientras APISearch funcione, el modal nativo NO existe en el DOM: vive dentro de
 * <template data-native-search-fallback>, cuyo contenido es inerte (los custom elements no se
 * actualizan dentro de un template). Asi <details-modal> no registra listeners y no puede
 * solaparse con la capa de APISearch, que era el bug de la tecla ESCAPE de 2024.
 *
 * Senal de salud: APISearch publica window.apisearch_ui con initialSetupPerformed === true.
 * APISearch va diferido hasta la primera interaccion del usuario (speed optimizer en
 * .stores/<code>/layout/theme.liquid), por eso se comprueba un rato despues de esa primera
 * interaccion y no en el load.
 *
 * OJO con el nombre del fichero: ese mismo optimizer neutraliza cualquier <script> cuyo src
 * matchee /apisearch/, asi que este NO puede llamarse "apisearch-*.js".
 */

(() => {
  // Red de seguridad para el unico caso que el listener de `error` no ve: el script carga
  // bien pero nunca llega a arrancar. Es generoso a proposito. spdr.js encola los scripts
  // diferidos y los carga EN SERIE (async=false + seq()), asi que APISearch puede empezar a
  // cargar varios segundos despues de la primera interaccion, detras de klaviyo, gorgias,
  // gtm, reviews.io... Un margen corto haria fallback con APISearch todavia sano y en cola.
  const BACKSTOP_MS = 8000;

  const container = document.querySelector('[data-apisearch-fallback]');
  if (!container) return;

  const template = container.querySelector('template[data-native-search-fallback]');
  const trigger = container.querySelector('[data-apisearch-trigger]');
  if (!template || !trigger) return;

  const apisearchReady = () => !!(window.apisearch_ui && window.apisearch_ui.initialSetupPerformed);

  let wantsOpen = false;
  let swapped = false;
  let armed = false;

  // Sustituye el contenedor por el modal nativo. Es un cambio de un solo sentido: al quitarlo
  // desaparece el elemento .apisearch-selector, asi que APISearch ya no tiene donde engancharse
  // y nunca pueden acabar los dos buscadores a la vez.
  function useNativeSearch() {
    if (swapped || apisearchReady()) return;
    swapped = true;

    const fragment = document.importNode(template.content, true);
    const modal = fragment.querySelector('details-modal');
    container.parentNode.replaceChild(fragment, container);

    // Si el usuario ya habia pulsado la lupa, abrimos el modal para no perder ese gesto.
    const summary = modal && modal.querySelector('summary');
    if (wantsOpen && summary) summary.click();
  }

  // Señal directa y sin espera: si el script de APISearch no carga (dominio bloqueado, CDN
  // caido, DNS, adblocker) el <script> emite `error`. Los errores de recurso no burbujean,
  // pero si se capturan en fase de captura sobre window.
  window.addEventListener(
    'error',
    (event) => {
      const el = event.target;
      if (el && el.tagName === 'SCRIPT' && /apisearch/.test(el.src || '')) useNativeSearch();
    },
    true
  );

  // Los mismos eventos con los que spdr.js reactiva los scripts diferidos.
  ['mouseover', 'keydown', 'wheel', 'touchstart', 'touchmove'].forEach((eventName) => {
    window.addEventListener(eventName, onFirstInteraction, { once: true, passive: true });
  });

  function onFirstInteraction() {
    if (armed) return;
    armed = true;
    setTimeout(useNativeSearch, BACKSTOP_MS);
  }

  trigger.addEventListener('click', () => {
    wantsOpen = true;
    // Si APISearch no esta listo en este instante tampoco va a atender este click: sus
    // widgets no estan montados y el gesto se pierde igual. Asi que no esperamos a la cola
    // de spdr.js, damos el buscador nativo ya en vez de dejar el icono muerto.
    // useNativeSearch() no hace nada si APISearch si esta listo.
    useNativeSearch();
  });
})();
